import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'

const ConsentContext = createContext(null)

const STORAGE_KEY = 'mag_privacy_consent'
const CONSENT_VERSION = 1
const CONSENT_MAX_AGE = 180 * 24 * 60 * 60 * 1000

const readStoredConsent = () => {
    try {
        const storedValue = window.localStorage.getItem(STORAGE_KEY)

        if (!storedValue) return null

        const storedConsent = JSON.parse(storedValue)
        const storedAt = new Date(storedConsent.updatedAt).getTime()
        const isValid =
            storedConsent.version === CONSENT_VERSION &&
            typeof storedConsent.spotify === 'boolean' &&
            typeof storedConsent.adobeFonts === 'boolean' &&
            Number.isFinite(storedAt) &&
            Date.now() - storedAt < CONSENT_MAX_AGE

        if (isValid) return storedConsent

        window.localStorage.removeItem(STORAGE_KEY)
    } catch (error) {
        // The website remains usable if storage is unavailable or corrupted.
    }

    return null
}

export function ConsentProvider({ children }) {
    const [consentState, setConsentState] = useState(() => {
        const consent = readStoredConsent()

        return {
            consent,
            isDialogOpen: consent === null,
        }
    })

    const saveConsent = useCallback(({ spotify, adobeFonts }) => {
        const consent = {
            version: CONSENT_VERSION,
            spotify,
            adobeFonts,
            updatedAt: new Date().toISOString(),
        }

        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(consent))
        } catch (error) {
            // Keep the choice for this page view even if storage is blocked.
        }

        setConsentState({ consent, isDialogOpen: false })
    }, [])

    const openSettings = useCallback(() => {
        setConsentState((current) => ({
            ...current,
            isDialogOpen: true,
        }))
    }, [])

    const closeSettings = useCallback(() => {
        setConsentState((current) => ({
            ...current,
            isDialogOpen: current.consent === null,
        }))
    }, [])

    useEffect(() => {
        const stylesheetId = 'mag-adobe-fonts'
        const existingStylesheet = document.getElementById(stylesheetId)

        if (consentState.consent?.adobeFonts && !existingStylesheet) {
            const stylesheet = document.createElement('link')
            stylesheet.id = stylesheetId
            stylesheet.rel = 'stylesheet'
            stylesheet.href = 'https://use.typekit.net/tiz3nor.css'
            document.head.appendChild(stylesheet)
        } else if (!consentState.consent?.adobeFonts && existingStylesheet) {
            existingStylesheet.remove()
        }
    }, [consentState.consent?.adobeFonts])

    const value = useMemo(
        () => ({
            consent: consentState.consent,
            isDialogOpen: consentState.isDialogOpen,
            saveConsent,
            openSettings,
            closeSettings,
        }),
        [closeSettings, consentState, openSettings, saveConsent]
    )

    return (
        <ConsentContext.Provider value={value}>
            {children}
        </ConsentContext.Provider>
    )
}

export function useConsent() {
    const context = useContext(ConsentContext)

    if (!context) {
        throw new Error('useConsent must be used within ConsentProvider')
    }

    return context
}
